import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/privacy.scss";

const effectiveDate = "16 september 2026";

const sections = [
  { id: "verantwoordelijke", label: "Wie is verantwoordelijk?" },
  { id: "gegevens", label: "Welke gegevens gebruikt de demo?" },
  { id: "doeleinden", label: "Waarom worden gegevens gebruikt?" },
  { id: "lokale-opslag", label: "Lokale opslag en cookies" },
  { id: "bewaartermijnen", label: "Bewaren en verwijderen" },
  { id: "derden", label: "Externe diensten en links" },
  { id: "beveiliging", label: "Beveiliging" },
  { id: "rechten", label: "Jouw rechten" },
  { id: "wijzigingen", label: "Wijzigingen en contact" },
];

const PolicyHeading = ({ number, id, children }) => (
  <div className="legal-page__section-heading">
    <span aria-hidden="true">{String(number).padStart(2, "0")}</span>
    <h2 id={id}>{children}</h2>
  </div>
);

function Privacy() {

  return (
    <main className="privacy legal-page app-page-enter">
      <header className="legal-page__hero">
        <div>
          <p className="eyebrow">Juridisch / Privacy</p>
          <h1>Privacy,<br />helder uitgelegd.</h1>
        </div>
        <div className="legal-page__intro">
          <p>
            Deze verklaring beschrijft hoe de frontend-demo van {siteConfig.displayName}
            omgaat met informatie die je tijdens het bekijken en testen invoert.
          </p>
          <dl>
            <div>
              <dt>Van kracht</dt>
              <dd>{effectiveDate}</dd>
            </div>
            <div>
              <dt>Versie</dt>
              <dd>1.0 / Demo</dd>
            </div>
          </dl>
        </div>
      </header>

      <div className="legal-page__notice" role="note">
        <span>Belangrijk</span>
        <p>
          Dit is een frontend-demonstratie zonder backend, echte bestellingen of
          betaalverwerking. Vul geen echte kaartgegevens of andere gevoelige informatie in.
        </p>
      </div>

      <div className="legal-page__layout">
        <aside className="legal-page__index">
          <nav aria-label="Inhoudsopgave privacyverklaring">
            <p>Inhoud</p>
            <ol>
              {sections.map((section, index) => (
                <li key={section.id}>
                  <a href={`#${section.id}`}>
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    {section.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        </aside>

        <article className="legal-page__body">
          <section className="legal-page__section" aria-labelledby="verantwoordelijke">
            <PolicyHeading number={1} id="verantwoordelijke">
              Wie is verantwoordelijk?
            </PolicyHeading>
            <p>
              {siteConfig.displayName} is een merk van {siteConfig.companyName}, de beheerder
              van www.domtanion.art. Vragen over privacy kun je richten aan onze
              contactgegevens onderaan deze pagina.
            </p>
            <p>Als je ons rechtstreeks mailt, ontvangen wij je e-mailadres, bericht en eventuele bijlagen om je vraag te beantwoorden. Deel alleen wat voor je vraag nodig is. Wij gebruiken deze correspondentie voor de afhandeling en bewaren haar niet langer dan daarvoor of voor toepasselijke wettelijke verplichtingen nodig is. Voor inzage, correctie of verwijdering kun je ons mailen.</p>
            <p>
              Omdat deze versie geen server, klantaccount of bestelsysteem heeft, ontvangt
              {" "}{siteConfig.displayName} via de demo zelf geen checkout- of betaalgegevens.
            </p>
          </section>

          <section className="legal-page__section" aria-labelledby="gegevens">
            <PolicyHeading number={2} id="gegevens">
              Welke gegevens gebruikt de demo?
            </PolicyHeading>
            <p>Afhankelijk van wat je test, kan de browser de volgende informatie verwerken:</p>
            <ul>
              <li>product-ID&apos;s, gekozen maten, kleuren en aantallen in je winkelmand;</li>
              <li>product-ID&apos;s die je aan je verlanglijst toevoegt;</li>
              <li>
                tekst die je tijdelijk in checkout- of contactvelden invoert, uitsluitend in
                het actieve browservenster;
              </li>
              <li>
                gebruikelijke technische verzoekgegevens bij het laden van externe bronnen,
                zoals IP-adres, browsertype en tijdstip.
              </li>
            </ul>
            <p>
              De demo maakt geen klantprofiel aan en verzendt geen adres-, kaart- of
              berichtgegevens naar een database.
            </p>
          </section>

          <section className="legal-page__section" aria-labelledby="doeleinden">
            <PolicyHeading number={3} id="doeleinden">
              Waarom worden gegevens gebruikt?
            </PolicyHeading>
            <p>
              Lokale gegevens worden alleen gebruikt om de winkelervaring te demonstreren:
              je mandje behouden, favorieten tonen, totalen berekenen en de checkoutflow
              simuleren. Er vindt geen profilering, advertentietargeting of geautomatiseerde
              besluitvorming plaats.
            </p>
            <p>
              Het contactformulier demonstreert alleen de invoer en verzendt geen bericht.
              Gebruik de e-maillink om rechtstreeks contact op te nemen.
            </p>
          </section>

          <section className="legal-page__section" aria-labelledby="lokale-opslag">
            <PolicyHeading number={4} id="lokale-opslag">
              Lokale opslag en cookies
            </PolicyHeading>
            <p>
              Het winkelmandje en de verlanglijst worden met <em>localStorage</em> op jouw
              eigen apparaat bewaard. Daardoor blijven gekozen producten zichtbaar wanneer
              je de pagina vernieuwt. Deze gegevens verlaten je browser niet. Meer informatie vind je in ons <Link to="/cookies">cookiebeleid</Link>.
            </p>
            <p>
              Deze demo plaatst zelf geen analyse- of advertentiecookies. De browser kan wel
              eigen technische gegevens of voorkeuren beheren, en externe bronnen kunnen
              onder hun eigen voorwaarden verzoekgegevens ontvangen.
            </p>
          </section>

          <section className="legal-page__section" aria-labelledby="bewaartermijnen">
            <PolicyHeading number={5} id="bewaartermijnen">
              Bewaren en verwijderen
            </PolicyHeading>
            <p>
              Tijdelijke formulierinhoud verdwijnt wanneer je de pagina verlaat of het
              browservenster sluit. Winkelmand- en verlanglijstgegevens blijven lokaal staan
              tot je ze via de interface verwijdert of de websitegegevens in je browser wist.
            </p>
            <p>
              Omdat er geen orderdatabase bestaat, kan deze demo geen bestelling, betaling
              of klantrecord bewaren of terugvinden.
            </p>
          </section>

          <section className="legal-page__section" aria-labelledby="derden">
            <PolicyHeading number={6} id="derden">
              Externe diensten en links
            </PolicyHeading>
            <p>
              De site kan lettertypes of andere publieke bronnen van externe aanbieders laden.
              De kaartlink en e-maillinks openen diensten van derden. Hun eigen
              privacyvoorwaarden gelden zodra je zo&apos;n dienst gebruikt.
            </p>
            <p>
              Er is geen betaalprovider gekoppeld. De checkout toont uitsluitend een
              interactieve vormsimulatie en verstuurt geen kaartnummer, CVC of betaling.
            </p>
          </section>

          <section className="legal-page__section" aria-labelledby="beveiliging">
            <PolicyHeading number={7} id="beveiliging">
              Beveiliging
            </PolicyHeading>
            <p>
              We beperken de demonstratie bewust tot lokale browserfunctionaliteit. Toch is
              geen enkele website volledig zonder risico. Gebruik daarom fictieve gegevens
              bij het testen en deel nooit wachtwoorden, identiteitsdocumenten of werkelijke
              betaalinformatie.
            </p>
          </section>

          <section className="legal-page__section" aria-labelledby="rechten">
            <PolicyHeading number={8} id="rechten">
              Jouw rechten
            </PolicyHeading>
            <p>
              Je kunt vragen om inzage of correctie van persoonsgegevens die wij over je
              bewaren. Onder de AVG kun je, waar van toepassing, ook vragen om
              verwijdering, beperking of overdracht van persoonsgegevens en bezwaar maken
              tegen verwerking. In deze frontend-demo worden zulke persoonsgegevens niet op
              een server opgeslagen.
            </p>
            <p>
              Denk je toch dat informatie bij ons terechtgekomen is? Mail dan naar{" "}
              <a href={`mailto:${siteConfig.contact.email}`}>
                {siteConfig.contact.email}
              </a>.
            </p>
          </section>

          <section className="legal-page__section" aria-labelledby="wijzigingen">
            <PolicyHeading number={9} id="wijzigingen">
              Wijzigingen en contact
            </PolicyHeading>
            <p>
              Wanneer deze demo later echte accounts, analytics, bestellingen of betalingen
              krijgt, moet deze verklaring vóór ingebruikname worden aangepast. De datum
              bovenaan laat zien wanneer deze versie voor het laatst is vastgesteld.
            </p>
            <address className="legal-page__contact">
              <strong>{siteConfig.companyName}</strong>
              <a href={siteConfig.website}>www.domtanion.art</a>
              <span>{siteConfig.contact.address}</span>
              <a href={`mailto:${siteConfig.contact.email}`}>
                {siteConfig.contact.email}
              </a>
              
            </address>
          </section>
        </article>
      </div>

      <nav className="legal-page__related" aria-label="Gerelateerde informatie">
        <div>
          <p className="eyebrow">Verder lezen</p>
          <h2>Heldere afspraken,<br />zonder kleine lettertjes.</h2>
        </div>
        <div>
          <Link to="/cookies">Cookiebeleid <span aria-hidden="true">↗</span></Link>
          <Link to="/faqs">FAQs <span aria-hidden="true">↗</span></Link>
          <Link to="/terms">Gebruiksvoorwaarden <span aria-hidden="true">↗</span></Link>
          <Link to="/shipping">Verzending <span aria-hidden="true">↗</span></Link>
          <Link to="/returns">Retourbeleid <span aria-hidden="true">↗</span></Link>
          <Link to="/contact">Contact <span aria-hidden="true">↗</span></Link>
        </div>
      </nav>
    </main>
  );
}

export default Privacy;
